import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { PageHeader } from '@uhg-abyss/web/ui/PageHeader';
import { styled } from '@uhg-abyss/web/tools/styled';
import { MediaQuery } from '@uhg-abyss/web/ui/MediaQuery';
import { useOverlay } from '@uhg-abyss/web/hooks/useOverlay';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { useWindowSize } from '@sde/widgets-core/src/tools/useWindowSize';
import { AppNavigation } from './AppNavigation';
import { TopBar } from './LogoActionBar/TopBar';
import { HeaderModals } from './HeaderModals';
import { AppSearch } from './LogoActionBar/AppSearch';
import { PayerSelect } from './LogoActionBar/PayerSelect';
import { ProviderSelectDrawer } from './LogoActionBar/ProviderSelectDrawer';
import { ProviderSelect } from './LogoActionBar/ProviderSelect';
import { PayerSelectDrawer } from './LogoActionBar/PayerSelectDrawer';
import { useOnboarding } from '@sde/widgets-core/src/redux/resources/onboarding';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';

import {
  PAGE_HEADER_CSS,
  TOOLBAR_STYLE,
  NAV_MENU_WRAPPER_DESKTOP_STYLE,
  NAV_MENU_WRAPPER_MOBILE_STYLE,
  BREAKPOINT_DESKTOP,
  VERTICAL_SPACER_HEIGHT,
  VERTICAL_SPACER_COLLAPSED_HEIGHT,
} from './constants';

const VerticalSpacer = styled('div', {
  height: VERTICAL_SPACER_HEIGHT,
});

const VerticalSpacerCollapsed = styled('div', {
  height: VERTICAL_SPACER_COLLAPSED_HEIGHT,
});

const DropdownWrapper = styled('div', {
  paddingLeft: '8px',
});

const FlexDiv = styled('div', {
  flex: '0.2 1 auto',
});

const FlexDivLessShrink = styled('div', {
  flex: '1 0.8 auto',
});

const JustifyRight = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
});

const StyledZIndexPaddedDiv = styled('div', {
  zIndex: 1150,
  paddingLeft: '6px',
});

export const Header = ({ setDigitalSecurityError, providerNotSaved }) => {
  const flyout = useOverlay('pageheader-model');
  const topBar = TopBar();
  const appNav = AppNavigation();
  const { width } = useWindowSize();
  const { user } = useUser.state();
  const { getOnboarding } = useOnboarding.actions();

  useEffect(() => {
    if (user?.uuid) {
      getOnboarding(user.uuid);
    }
  }, [user?.uuid, getOnboarding]);

  const isDesktop = width > BREAKPOINT_DESKTOP;

  return (
    <React.Fragment>
      <div style={{ position: 'fixed', zIndex: 1075, width: '100%', top: 0 }}>
        <PageHeader
          model="pageheader-model"
          extraDrawerContent={<AppSearch onSearch={() => flyout.close()} />}
          topMenuItems={topBar}
          bottomMenuItems={appNav}
          logoRedirect="/"
          css={PAGE_HEADER_CSS}
        >
          <PageHeader.Toolbar space={10} style={TOOLBAR_STYLE}>
            <FlexDiv>
              <MediaQuery largerThan={BREAKPOINT_DESKTOP}>
                <StyledZIndexPaddedDiv>
                  <AppSearch />
                </StyledZIndexPaddedDiv>
              </MediaQuery>
            </FlexDiv>
            <FlexDivLessShrink>
              <JustifyRight data-testid="payer-provider-wrapper">
                <DropdownWrapper>
                  <Choose>
                    <When condition={getFeature('PAYER_SELECT_REDESIGN')}>
                      <PayerSelectDrawer />
                    </When>
                    <Otherwise>
                      <PayerSelect />
                    </Otherwise>
                  </Choose>
                </DropdownWrapper>
                <DropdownWrapper>
                  <Choose>
                    <When condition={getFeature('PROVIDER_SELECT_REDESIGN')}>
                      <ProviderSelectDrawer
                        providerNotSaved={providerNotSaved}
                      />
                    </When>
                    <Otherwise>
                      <ProviderSelect
                        setDigitalSecurityError={setDigitalSecurityError}
                      />
                    </Otherwise>
                  </Choose>
                </DropdownWrapper>
              </JustifyRight>
            </FlexDivLessShrink>
          </PageHeader.Toolbar>
        </PageHeader>
        <div
          style={
            isDesktop
              ? NAV_MENU_WRAPPER_DESKTOP_STYLE
              : NAV_MENU_WRAPPER_MOBILE_STYLE
          }
          data-testid="nav-menu-wrapper"
        />
      </div>

      <MediaQuery
        largerThan={BREAKPOINT_DESKTOP}
        css={{
          '@media (min-width: 984px)': {
            display: 'contents !important',
          },
        }}
      >
        <VerticalSpacer />
      </MediaQuery>

      <MediaQuery smallerThan={BREAKPOINT_DESKTOP}>
        <VerticalSpacerCollapsed />
      </MediaQuery>

      <HeaderModals />
    </React.Fragment>
  );
};

Header.propTypes = {
  setDigitalSecurityError: PropTypes.func.isRequired,
  providerNotSaved: PropTypes.bool,
};
